import { PropRuntimeGraph } from '../../../../../shared/types/PropRuntimeGraph';
import { PropActionResult, PropRuntimeEventReason, RuntimePropInstance, RuntimeRarityWeightRule } from '../PropMechanicsTypes';
import { actionFail, actionSuccess, RuntimeActionNodeBase } from './RuntimeNodeBase';
import { pickRandomItems } from './RuntimeNodeUtils';

/**
 * 成长动作节点。
 *
 * 使目标永久增加或减少魔力产出。
 */
export class ActionGrowNode extends RuntimeActionNodeBase {
    /**
     * 执行成长动作。
     *
     * @returns 有目标时成功，并将目标作为 each 分支目标。
     */
    public execute(): PropActionResult {
        const targets = this.actionTargets();
        const changed: RuntimePropInstance[] = [];
        for (const target of targets) {
            this.withActionTarget(target, () => {
                if (!this.isActionConditionMatched()) return;
                const amount = this.inputNumber('amount', 0);
                target.grow += amount;
                changed.push(target);
                this.context.mutations.push({ kind: 'grow', itemId: target.id, grow: target.grow, delta: amount, sourceId: this.context.owner.id });
                this.context.effects.push({ kind: 'grow', sourceId: this.context.owner.id, targetIds: [target.id], amount });
            });
        }
        return changed.length ? actionSuccess(changed) : actionFail();
    }
}

/**
 * 额外产出动作节点。
 *
 * 立即为当前回合增加魔力，支持固定数量和倍率两种输入。
 */
export class ActionExtraYieldNode extends RuntimeActionNodeBase {
    /**
     * 执行额外产出动作。
     *
     * @returns 有目标时成功，魔力变化记录在运行时状态中。
     */
    public execute(): PropActionResult {
        const targets = this.actionTargets();
        let total = 0;
        const changed: RuntimePropInstance[] = [];
        for (const target of targets) {
            const delta = this.withActionTarget(target, () => {
                if (!this.isActionConditionMatched()) return undefined;
                const amount = this.inputNumber('amount', 0);
                const multiplier = this.inputNumber('multiplier', 0);
                const baseYield = Number(target.graph.yield || 0);
                const grow = Number(target.grow || 0);
                const delta = (baseYield + grow) * multiplier + amount;
                changed.push(target);
                this.context.addMagic(delta, this.context.owner, [target]);
                this.context.effects.push({
                    kind: 'extraYield',
                    sourceId: this.context.owner.id,
                    targetIds: [target.id],
                    amount: delta,
                });
                return delta;
            });
            if (delta !== undefined) total += delta;
        }
        return changed.length ? actionSuccess(changed, total) : actionFail();
    }
}

/**
 * 永久加成动作节点。
 *
 * 对一批目标写入持续加成；同一 `mutexTag` 的效果只允许生效一次。
 */
export class ActionPermanentBoostNode extends RuntimeActionNodeBase {
    /**
     * 执行永久加成动作。
     *
     * @returns 首次应用互斥效果且存在目标时成功。
     */
    public execute(): PropActionResult {
        if (!this.isActionConditionMatched()) return actionFail();
        const rule = this.context.registerPermanentBoostRule(this.node);
        if (!rule) return actionFail();
        this.context.effects.push({ kind: 'permanentBoost', sourceId: this.context.owner.id, targetIds: [], ruleId: rule.id });
        this.context.emit({ type: 'permanentBoostRulesChanged', source: this.context.owner, reason: 'actionPermanentBoost' });
        return actionSuccess([this.context.owner]);
    }
}

/**
 * 净化动作节点。
 *
 * 清除目标身上的负面效果标记。
 */
export class ActionPurifyNode extends RuntimeActionNodeBase {
    /**
     * 执行净化动作。
     *
     * @returns 有目标时成功。
     */
    public execute(): PropActionResult {
        const targets = this.actionTargets();
        const changed: RuntimePropInstance[] = [];
        for (const target of targets) {
            this.withActionTarget(target, () => {
                if (!this.isActionConditionMatched()) return;
                target.negativeEffects = [];
                target.purified = true;
                changed.push(target);
                this.context.mutations.push({ kind: 'purified', itemId: target.id, purified: true, sourceId: this.context.owner.id });
                this.context.effects.push({ kind: 'purify', sourceId: this.context.owner.id, targetIds: [target.id] });
            });
        }
        return changed.length ? actionSuccess(changed) : actionFail();
    }
}

/**
 * 保护动作节点。
 *
 * 为目标增加一层消除保护。
 */
export class ActionProtectNode extends RuntimeActionNodeBase {
    /**
     * 执行保护动作。
     *
     * @returns 有目标时成功。
     */
    public execute(): PropActionResult {
        const targets = this.actionTargets();
        const changed: RuntimePropInstance[] = [];
        for (const target of targets) {
            this.withActionTarget(target, () => {
                if (!this.isActionConditionMatched()) return;
                const previousStacks = target.protectStacks;
                target.protectStacks = Math.max(target.protectStacks, 1);
                changed.push(target);
                this.context.mutations.push({ kind: 'protect', itemId: target.id, protectStacks: target.protectStacks, delta: target.protectStacks - previousStacks, sourceId: this.context.owner.id });
                this.context.effects.push({ kind: 'protect', sourceId: this.context.owner.id, targetIds: [target.id], stacks: 1 });
            });
        }
        return changed.length ? actionSuccess(changed) : actionFail();
    }
}

/**
 * 加速动作节点。
 *
 * 调整目标的倒计时偏移值。
 */
export class ActionAccelerateNode extends RuntimeActionNodeBase {
    /**
     * 执行加速动作。
     *
     * @returns 有目标时成功。
     */
    public execute(): PropActionResult {
        const targets = this.actionTargets();
        const changed: RuntimePropInstance[] = [];
        for (const target of targets) {
            this.withActionTarget(target, () => {
                if (!this.isActionConditionMatched()) return;
                const amount = this.inputNumber('amount', 0);
                const current = Number(target.meta.turnOffset ?? 0);
                target.meta.turnOffset = current + amount;
                adjustTargetTurnStartTimers(target, amount, this.context.owner);
                changed.push(target);
                this.context.mutations.push({ kind: 'meta', itemId: target.id, key: 'turnOffset', value: target.meta.turnOffset, sourceId: this.context.owner.id });
                this.context.effects.push({ kind: 'accelerate', sourceId: this.context.owner.id, targetIds: [target.id], amount });
            });
        }
        return changed.length ? actionSuccess(changed) : actionFail();
    }
}

/**
 * 转化动作节点。
 *
 * 将目标实例的当前原型替换为候选原型之一。
 */
export class ActionTransformNode extends RuntimeActionNodeBase {
    /**
     * 执行转化动作。
     *
     * @returns 同时存在被转化目标和目标原型时成功。
     */
    public execute(): PropActionResult {
        const targets = this.actionTargets();
        const changed: RuntimePropInstance[] = [];
        for (const target of targets) {
            this.withActionTarget(target, () => {
                if (!this.isActionConditionMatched()) return;
                const prototypes = readTransformPrototypeTargets(this);
                if (!prototypes.length) return;
                const previousPrototype = target.graph;
                const graph = prototypes[this.context.rng.int(0, prototypes.length)];
                target.graph = graph;
                resetTransformedTargetState(target);
                this.context.rebuildNodeRuntime(target);
                this.context.mutations.push({ kind: 'graph', itemId: target.id, prototypeId: graph._id, sourceId: this.context.owner.id });
                this.context.emit({
                    type: 'propTransformed',
                    item: target,
                    target,
                    source: this.context.owner,
                    prototype: graph,
                    prototypeId: graph._id,
                    previousPrototype,
                    previousPrototypeId: previousPrototype._id,
                    row: target.row,
                    col: target.col,
                    reason: 'actionTransform',
                });
                emitTransformedTurnStartTimers(target, this.context.owner);
                this.context.effects.push({ kind: 'transform', sourceId: this.context.owner.id, targetIds: [target.id], prototypeIds: [graph._id] });
                changed.push(target);
            });
        }
        return changed.length ? actionSuccess(changed) : actionFail();
    }
}

/**
 * 生成动作节点。
 *
 * 根据原型输入创建新的运行时实例；有位置输入时生成到对应位置，否则加入材料库。
 */
export class ActionSpawnNode extends RuntimeActionNodeBase {
    /**
     * 执行生成动作。
     *
     * @returns 成功生成至少一个实例时成功。
     */
    public execute(): PropActionResult {
        const spawned: RuntimePropInstance[] = [];
        if (!this.isActionConditionMatched()) return actionFail();
        const prototypes = readPrototypeTargets(this);
        const amount = Math.max(0, Math.floor(this.inputNumber('amount', 1)));
        if (!prototypes.length || amount <= 0) return actionFail();
        const positions = this.inputTargets('position', []);
        for (let i = 0; i < amount; i++) {
            const graph = prototypes[this.context.rng.int(0, prototypes.length)];
            const position = positions[i % Math.max(positions.length, 1)];
            const item = this.context.acquireProp(graph, position
                ? { positions: [position], reason: 'actionSpawn' }
                : { reason: 'actionSpawn' });
            this.context.effects.push({ kind: 'spawn', sourceId: this.context.owner.id, prototypeIds: [graph._id], targetIds: [item.id], count: 1 });
            spawned.push(item);
        }
        return spawned.length ? actionSuccess(spawned) : actionFail();
    }
}

/**
 * 触发表现动作节点。
 *
 * 该节点只用于触发动作节点上配置的动画和特效，
 * 不修改魔力、材料库、盘面或道具状态，也不参与描述文本和强度计算。
 */
export class ActionTriggerVisualNode extends RuntimeActionNodeBase {
    /** 执行纯表现动作，仅计算条件和目标表现对象。 */
    public execute(): PropActionResult {
        const targets = this.actionTargets();
        if (!targets.length) return this.isActionConditionMatched() ? actionSuccess([]) : actionFail();
        const matched: RuntimePropInstance[] = [];
        for (const target of targets) {
            this.withActionTarget(target, () => {
                if (this.isActionConditionMatched()) matched.push(target);
            });
        }
        return matched.length ? actionSuccess(matched) : actionFail();
    }
}


/**
 * 消除动作节点。
 *
 * 消除目标实例；如果目标有保护层，则目标不会死亡，但动作仍视为成功。
 */
export class ActionEliminateNode extends RuntimeActionNodeBase {
    /**
     * 执行消除动作。
     *
     * @returns 选中至少一个目标时成功；受保护目标也计入成功。
     */
    public execute(): PropActionResult {
        const targets = this.actionTargets();
        const amountNodeIds = this.context.readInputNodeIds(this.node, 'amount');
        const selected: RuntimePropInstance[] = [];
        const orderedTargets = amountNodeIds.length ? pickRandomItems(targets, targets.length, this.context.rng) : targets;
        for (const target of orderedTargets) {
            this.withActionTarget(target, () => {
                if (!this.isActionConditionMatched()) return;
                const amount = amountNodeIds.length ? Math.max(0, Math.floor(this.inputNumber('amount', targets.length))) : targets.length;
                if (selected.length < amount) selected.push(target);
            });
        }
        const eliminated: RuntimePropInstance[] = [];
        const blocked: RuntimePropInstance[] = [];
        for (const target of selected) {
            if (target.protectStacks > 0) {
                blocked.push(target);
                this.context.effects.push({
                    kind: 'eliminate',
                    sourceId: this.context.owner.id,
                    targetIds: [target.id],
                    blockedIds: [target.id],
                });
                continue;
            }
            const removedRow = target.row;
            const removedCol = target.col;
            target.alive = false;
            this.context.removeFromBoard(target);
            removeId(this.context.state.materialLibrary, target.id);
            removeId(this.context.state.recipes, target.id);
            removeId(this.context.state.consumables, target.id);
            eliminated.push(target);
            this.context.mutations.push({ kind: 'alive', itemId: target.id, alive: false, sourceId: this.context.owner.id });
            this.context.emit({ type: 'propRemoved', item: target, target, source: this.context.owner, row: removedRow, col: removedCol, reason: 'actionEliminate' });
            this.context.emit({ type: 'propEliminated', item: target, target, source: this.context.owner, row: removedRow, col: removedCol, reason: 'actionEliminate' });
            this.context.disposeNodeRuntime(target);
            this.context.effects.push({
                kind: 'eliminate',
                sourceId: this.context.owner.id,
                targetIds: [target.id],
                blockedIds: [],
            });
        }
        return selected.length ? actionSuccess(selected) : actionFail();
    }
}

/**
 * 收集动作节点。
 *
 * 从目标处收集魔力到来源实例自身，目标不会减少魔力。
 */
export class ActionCollectNode extends RuntimeActionNodeBase {
    /**
     * 执行收集动作。
     *
     * @returns 有目标时成功。
     */
    public execute(): PropActionResult {
        const targets = this.actionTargets();
        const changed: RuntimePropInstance[] = [];
        let total = 0;
        for (const target of targets) {
            const amount = this.withActionTarget(target, () => {
                if (!this.isActionConditionMatched()) return undefined;
                const amount = this.inputNumber('amount', 0);
                this.context.owner.collectedMagic += amount;
                changed.push(target);
                this.context.emit({
                    type: 'propCollected',
                    item: this.context.owner,
                    source: this.context.owner,
                    target,
                    targetIds: [target.id],
                    magicDelta: amount,
                    itemId: this.context.owner.id,
                    reason: 'actionCollect',
                });
                this.context.effects.push({ kind: 'collect', sourceId: this.context.owner.id, targetIds: [target.id], amount });
                return amount;
            });
            if (amount !== undefined) total += amount;
        }
        if (changed.length) this.context.mutations.push({ kind: 'meta', itemId: this.context.owner.id, key: 'collectedMagic', value: this.context.owner.collectedMagic, sourceId: this.context.owner.id });
        return changed.length ? actionSuccess(changed, total) : actionFail();
    }
}

/**
 * 收获动作节点。
 *
 * 将来源实例已经收集的魔力释放到当前回合产出。
 */
export class ActionHarvestNode extends RuntimeActionNodeBase {
    /**
     * 执行收获动作。
     *
     * @returns 已收集魔力不为 0 时成功。
     */
    public execute(): PropActionResult {
        if (!this.isActionConditionMatched()) return actionFail();
        const amount = this.context.owner.collectedMagic;
        if (amount === 0) return actionFail();
        this.context.owner.collectedMagic = 0;
        this.context.addMagic(amount, this.context.owner, [this.context.owner]);
        this.context.effects.push({ kind: 'harvest', sourceId: this.context.owner.id, amount });
        return actionSuccess([this.context.owner], amount);
    }
}

/**
 * 品质权重动作节点。
 *
 * 调整指定品质在抽取时的权重倍率。
 */
export class ActionRarityWeightNode extends RuntimeActionNodeBase {
    /**
     * 执行品质权重调整。
     *
     * @returns 品质和倍率合法时成功。
     */
    public execute(): PropActionResult {
        if (!this.isActionConditionMatched()) return actionFail();
        const rarity = Math.floor(this.inputNumber('rarity', 0));
        const multiplier = this.inputNumber('multiplier', 1);
        if (rarity <= 0 || multiplier <= 0) return actionFail();

        const ruleId = `${this.context.owner.id}:${this.node.id}`;
        const persistent = this.context.event.type !== 'propAcquired';
        const rules = this.context.state.rarityWeightRules;
        const existingIndex = rules.findIndex((rule) => rule.id === ruleId);
        const rule = {
            id: ruleId,
            sourceId: this.context.owner.id,
            actionNodeId: this.node.id,
            rarity,
            multiplier,
            persistent,
        };
        if (existingIndex >= 0) rules[existingIndex] = rule;
        else rules.push(rule);
        recomputeRarityWeightMultipliers(this.context.state.rarityWeightRules, this.context.state.rarityWeightMultipliers);

        if (!persistent) {
            this.context.registerReleaseCallback(() => {
                const index = this.context.state.rarityWeightRules.findIndex((item) => item.id === ruleId);
                if (index >= 0) {
                    this.context.state.rarityWeightRules.splice(index, 1);
                    recomputeRarityWeightMultipliers(this.context.state.rarityWeightRules, this.context.state.rarityWeightMultipliers);
                    this.context.emit({ type: 'rarityWeightChanged', source: this.context.owner, reason: 'releaseRarityWeight' });
                }
            });
        }

        this.context.effects.push({ kind: 'rarityWeight', sourceId: this.context.owner.id, rarity, multiplier });
        this.context.emit({ type: 'rarityWeightChanged', source: this.context.owner, reason: 'actionRarityWeight' });
        return actionSuccess([this.context.owner]);
    }
}

/**
 * 延长调和动作节点。
 *
 * 当前委托增加一次调和轮数，同一 onceKey 每份委托只生效一次。
 */
export class ActionExtendCommissionNode extends RuntimeActionNodeBase {
    /**
     * 执行延长调和动作。
     *
     * @returns 当前委托首次触发该 key 时成功。
     */
    public execute(): PropActionResult {
        const key = String(this.node.data?.onceKey ?? 'extendCommission');
        if (!this.isActionConditionMatched()) return actionFail();
        if (this.context.state.commissionOnceKeys.has(key)) return actionFail();
        this.context.state.commissionOnceKeys.add(key);
        this.context.state.extraCommissionRounds += 1;
        this.context.effects.push({ kind: 'extendCommission', sourceId: this.context.owner.id, count: 1 });
        this.context.emit({ type: 'commissionRoundLimitChanged', source: this.context.owner, reason: 'actionExtendCommission' });
        return actionSuccess([this.context.owner]);
    }
}

/**
 * 交换位置动作节点。
 *
 * 将目标实例和位置输入实例交换盘面坐标。
 */
export class ActionSwapPositionNode extends RuntimeActionNodeBase {
    /**
     * 执行交换位置动作。
     *
     * @returns 同时存在目标和位置输入时成功。
     */
    public execute(): PropActionResult {
        const targets = this.actionTargets()
            .filter((target) => isSwappableBoardItem(target));
        const moved: RuntimePropInstance[] = [];
        const usedPositionIds = new Set<string>();
        for (const target of targets) {
            this.withActionTarget(target, () => {
                if (!this.isActionConditionMatched()) return;
                const positions = pickRandomItems(
                    this.inputTargets('positions', [])
                        .filter((position) => isSwappableBoardItem(position) && position.id !== target.id && !usedPositionIds.has(position.id)),
                    Number.MAX_SAFE_INTEGER,
                    this.context.rng,
                );
                if (!positions.length) return;
                const position = positions[0];
                usedPositionIds.add(position.id);
                if (!swapPositions(target, position, this)) return;
                this.context.effects.push({ kind: 'swapPosition', sourceId: this.context.owner.id, targetIds: [target.id, position.id] });
                moved.push(target);
            });
        }
        return moved.length ? actionSuccess(moved) : actionFail();
    }
}

function isSwappableBoardItem(item: RuntimePropInstance): boolean {
    return Boolean(item.alive && item.row >= 0 && item.col >= 0);
}

function readPrototypeTargets(node: RuntimeActionNodeBase): PropRuntimeGraph[] {
    const fromPrototypePort = node.readPrototypeInputGraphs('prototypes');
    if (fromPrototypePort.length) return fromPrototypePort;
    return node.readPrototypeInputGraphs('targets');
}

function recomputeRarityWeightMultipliers(
    rules: RuntimeRarityWeightRule[],
    multipliers: Record<number, number>,
): void {
    for (const key of Object.keys(multipliers)) delete multipliers[Number(key)];
    for (const rule of rules) {
        if (rule.rarity <= 0 || rule.multiplier <= 0) continue;
        multipliers[rule.rarity] = (multipliers[rule.rarity] ?? 1) * rule.multiplier;
    }
}

function readTransformPrototypeTargets(node: RuntimeActionNodeBase): PropRuntimeGraph[] {
    return node.readPrototypeInputGraphs('prototypes');
}

function resetTransformedTargetState(target: RuntimePropInstance): void {
    target.grow = 0;
    target.triggerCount = 0;
    target.useCount = 0;
    target.protectStacks = 0;
    target.collectedMagic = 0;
    target.meta = {};
    target.negativeEffects = [];
    delete target.purified;
}

function swapPositions(left: RuntimePropInstance, right: RuntimePropInstance, node: RuntimeActionNodeBase): boolean {
    const swapped = node.context.swapBoardItems(left, right, node.context.owner, 'actionSwapPosition');
    if (!swapped) return false;
    node.context.mutations.push({ kind: 'position', itemId: left.id, row: left.row, col: left.col, sourceId: node.context.owner.id });
    node.context.mutations.push({ kind: 'position', itemId: right.id, row: right.row, col: right.col, sourceId: node.context.owner.id });
    return true;
}

function removeId(list: string[], id: string): void {
    const index = list.indexOf(id);
    if (index >= 0) list.splice(index, 1);
}

function adjustTargetTurnStartTimers(target: RuntimePropInstance, amount: number, source: RuntimePropInstance): void {
    target.nodeRuntime?.nodes.forEach((node) => {
        if (node.node.type !== 'eventTurnStart') return;
        const timerNode = node as unknown as {
            adjustTimer?: (value: number, source?: RuntimePropInstance) => void;
        };
        if (typeof timerNode.adjustTimer === 'function') timerNode.adjustTimer(amount, source);
    });
}

function emitTransformedTurnStartTimers(target: RuntimePropInstance, source: RuntimePropInstance): void {
    target.nodeRuntime?.nodes.forEach((node) => {
        if (node.node.type !== 'eventTurnStart') return;
        const timerNode = node as unknown as {
            emitCurrentTimer?: (reason: PropRuntimeEventReason, source?: RuntimePropInstance) => void;
        };
        if (typeof timerNode.emitCurrentTimer === 'function') timerNode.emitCurrentTimer('transformed', source);
    });
}
